import { useIntl } from 'react-intl';
import type { ListBuyerContractTemplatesResponse } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';

type Templates = ListBuyerContractTemplatesResponse['data'];
type Template = Templates[number];

const DASH = '—';
const BERLIN = 'Europe/Berlin';

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h2 className="text-sm font-semibold uppercase tracking-wide text-text-tertiary">{children}</h2>;
}

function DefRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-text-tertiary">{label}</dt>
      <dd className="text-text-primary">{children}</dd>
    </>
  );
}

type BuyerContractSectionProps = {
  readonly templates: Templates | null;
};

export function BuyerContractSection({ templates }: BuyerContractSectionProps) {
  const t = useTranslations('pages.admin.workspaces.buyer-contract');
  const tRoot = useTranslations();
  const intl = useIntl();

  const active = templates?.find((row) => row.active);

  if (templates === null || active === undefined) {
    return (
      <section className="space-y-2">
        <SectionHeading>{t('heading')}</SectionHeading>
        <p className="text-sm text-danger-600">{t('load-error')}</p>
      </section>
    );
  }

  const nameOf = (row: Template) =>
    row.kind === 'STANDARD' ? tRoot('common.buyer-contract-templates.standard-name') : (row.name ?? DASH);
  const inactiveOwn = templates.find((row) => row.kind === 'OWN' && !row.active);
  const activeLine =
    active.kind === 'STANDARD'
      ? t('active', { name: nameOf(active), version: active.version })
      : t('active-own', {
          name: nameOf(active),
          version: active.version,
          date: intl.formatDate(active.updatedAt, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            timeZone: BERLIN,
          }),
          time: intl.formatTime(active.updatedAt, { hour: '2-digit', minute: '2-digit', timeZone: BERLIN }),
          member: active.savedByName ?? DASH,
        });

  return (
    <section className="space-y-2">
      <SectionHeading>{t('heading')}</SectionHeading>
      <dl className="grid grid-cols-[180px_1fr] gap-y-2 text-base">
        <DefRow label={t('heading')}>
          <div>{activeLine}</div>
          {inactiveOwn === undefined ? null : (
            <div className="text-text-secondary">
              {t('inactive-own', { name: nameOf(inactiveOwn), version: inactiveOwn.version })}
            </div>
          )}
        </DefRow>
      </dl>
    </section>
  );
}
